import axios from 'axios';

// API base configuration
const api = axios.create({
  baseURL: 'https://mongobyte-hc73.onrender.com/api/v1',
  timeout: 30000 // 30 seconds timeout
});

// Add auth token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Error handling utility
const handleApiError = (error) => {
  if (error.response) {
    const message = error.response.data?.message || 'An error occurred';
    const status = error.response.status;

    switch (status) {
      case 400:
        throw new Error(`Invalid request: ${message}`);
      case 401:
        throw new Error('Authentication required');
      case 403:
        throw new Error('Not authorized');
      case 404:
        throw new Error('Resource not found');
      default:
        throw new Error(message);
    }
  } else if (error.request) {
    // Request made but no response
    throw new Error('Network error - no response received');
  } else {
    // Error in request setup
    throw new Error('Failed to make request');
  }
};

// Order Status Constants - Capitalized as per backend enum
export const OrderStatus = {
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  DELIVERED: 'Delivered',
  CANCELED: 'Canceled',
  FEE_REQUESTED: 'Fee Requested'
};

/**
 * ORDER MANAGEMENT APIs
 */

// Fetch restaurant orders
export const fetchOrders = async (restaurantCustomId) => {
  try {
    if (!restaurantCustomId) {
      throw new Error('Restaurant ID is required');
    }

    const response = await api.get(`/orders/restaurant/${restaurantCustomId}`);
    // Handle both response formats: {orders: [...]} and direct array [...]
    return response.data.orders || response.data || [];
  } catch (error) {
    throw handleApiError(error);
  }
};

// Confirm order
export const confirmOrder = async (orderId) => {
  try {
    const response = await api.post(`/orders/${orderId}/confirm`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Mark order as delivered
export const markOrderAsDelivered = async (orderId) => {
  try {
    const response = await api.patch(`/orders/deliver/${orderId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * ADVANCED ORDERS APIs
 */

// Get scheduled orders
export const getScheduledOrders = async (params = {}) => {
  try {
    const response = await api.get('/restaurants/advanced-orders/scheduled', { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Update scheduled order status
export const updateScheduledOrderStatus = async (orderId, status, reason) => {
  try {
    const response = await api.put(`/restaurants/advanced-orders/scheduled/${orderId}/status`, {
      status,
      reason
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Get group orders
export const getGroupOrders = async (params = {}) => {
  try {
    const response = await api.get('/restaurants/advanced-orders/group', { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Update group order status
export const updateGroupOrderStatus = async (orderId, status, reason) => {
  try {
    const response = await api.put(`/restaurants/advanced-orders/group/${orderId}/status`, {
      status,
      reason
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Get advanced orders statistics
export const getAdvancedOrderStats = async () => {
  try {
    const response = await api.get('/restaurants/advanced-orders/stats');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * LANDMARK MANAGEMENT APIs
 */

// Get all university landmarks
export const getAllLandmarks = async () => {
  try {
    // Get list of universities first
    const response = await api.get('/universities');
    const universities = response.data.universities || [];
    
    // Fetch landmarks for each university
    const landmarkPromises = universities.map(uni => 
      api.get(`/universities/${uni._id}/landmarks`)
        .then(res => ({
          universityId: uni._id,
          universityName: uni.name,
          landmarks: res.data.landmarks || []
        }))
        .catch(() => ({
          universityId: uni._id,
          universityName: uni.name,
          landmarks: []
        }))
    );
    
    const results = await Promise.all(landmarkPromises);
    return results.filter(result => result.landmarks.length > 0);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Get landmarks by university
export const getLandmarksByUniversity = async (universityId) => {
  try {
    const response = await api.get(`/universities/${universityId}/landmarks`);
    return response.data.landmarks;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Search landmarks
export const searchLandmarks = async (query, options = {}) => {
  try {
    const params = { q: query, ...options };
    // First get all universities
    const universities = await api.get('/universities');
    const uniList = universities.data.universities || [];

    // Search in each university's landmarks
    const searchPromises = uniList.map(uni =>
      api.get(`/universities/${uni._id}/landmarks`)
        .then(res => {
          const landmarks = res.data.landmarks || [];
          return landmarks
            .filter(landmark => 
              landmark.toLowerCase().includes(query.toLowerCase())
            )
            .map(landmark => ({
              name: landmark,
              university: uni.name,
              universityId: uni._id
            }));
        })
        .catch(() => [])
    );

    const results = await Promise.all(searchPromises);
    return results.flat();
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * DELIVERY FEE MANAGEMENT APIs
 */

// Set delivery fee for a landmark
export const setDeliveryFees = async (customId, landmark, fee) => {
  try {
    const response = await api.post('/delivery-fees/set', {
      restaurantId: customId,
      landmark,
      fee
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Get restaurant's delivery fees
export const getDeliveryFees = async (restaurantCustomId) => {
  try {
    const response = await api.get(`/delivery-fees/restaurant/${restaurantCustomId}`);
    return response.data.fees;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * RESTAURANT MANAGEMENT APIs
 */

// Get restaurant profile
export const getRestaurantProfile = async (restaurantId) => {
  try {
    const response = await api.get(`/restaurants/${restaurantId}/profile`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Update restaurant information
export const updateRestaurantInfo = async (restaurantId, restaurantData) => {
  try {
    const response = await api.put(`/restaurants/${restaurantId}`, restaurantData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Toggle restaurant active status
export const toggleRestaurantStatus = async (restaurantId, isActive) => {
  try {
    const response = await api.patch(`/restaurants/${restaurantId}/toggle-active`, { isActive });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Upload restaurant image
export const uploadRestaurantImage = async (imageBase64) => {
  try {
    const response = await api.post('/restaurants/upload-image', { image: imageBase64 });
    return response.data.url;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Request withdrawal
export const createWithdrawalRequest = async (restaurantName, amount) => {
  try {
    const response = await api.post('/restaurants/withdraw', {
      restaurantName,
      amount
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Get withdrawal history
export const getWithdrawalHistory = async (restaurantId) => {
  try {
    const response = await api.get(`/withdrawals/restaurant/${restaurantId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * REVENUE MANAGEMENT APIs
 */

// Get revenue statistics
export const getRevenueStats = async (restaurantId, params = {}) => {
  try {
    const response = await api.get(`/restaurants/${restaurantId}/revenue`, { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * MEAL MANAGEMENT APIs
 */

// Upload meal image
export const uploadMealImage = async (imageBase64) => {
  try {
    const response = await api.post('/meals/upload-image', { image: imageBase64 });
    return response.data.url;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Get restaurant meals
export const getRestaurantMeals = async () => {
  try {
    const response = await api.get('/meals/me');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Create new meal
export const createMeal = async (restaurantId, mealData) => {
  try {
    const response = await api.post(`/meals/${restaurantId}/create`, mealData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Update meal
export const updateMeal = async (mealId, mealData) => {
  try {
    const response = await api.put(`/meals/${mealId}`, mealData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Delete meal
export const deleteMeal = async (mealId) => {
  try {
    const response = await api.delete(`/meals/${mealId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Batch add meals
export const batchAddMeals = async (restaurantId, meals) => {
  try {
    const response = await api.post('/meals/batch', { restaurantId, meals });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * RATING MANAGEMENT APIs
 */

// Rating-specific error handling
const handleRatingApiError = (error) => {
  if (error.response) {
    const { status, data } = error.response;
    switch (status) {
      case 400:
        // Handle specific validation errors
        if (data.errors && Array.isArray(data.errors)) {
          const errorMessages = data.errors.map(err => err.message || err).join(', ');
          throw new Error(`Invalid rating data: ${errorMessages}`);
        }
        throw new Error(data.message || 'Invalid rating data provided');
      case 401:
        // Clear token if unauthorized
        localStorage.removeItem('token');
        throw new Error('Please log in to rate restaurants');
      case 403:
        if (data.message?.includes('already rated')) {
          throw new Error('You have already rated this restaurant. You can edit your existing rating.');
        }
        if (data.message?.includes('order not eligible')) {
          throw new Error('You can only rate restaurants from completed orders.');
        }
        if (data.message?.includes('admin')) {
          throw new Error('Admin privileges required for this action');
        }
        throw new Error('You are not authorized to perform this action');
      case 404:
        if (data.message?.includes('restaurant')) {
          throw new Error('Restaurant not found');
        }
        if (data.message?.includes('order')) {
          throw new Error('Order not found');
        }
        if (data.message?.includes('rating')) {
          throw new Error('Rating not found');
        }
        throw new Error('Resource not found');
      case 409:
        throw new Error('You have already rated this restaurant. You can edit your existing rating instead.');
      case 422:
        throw new Error(data.message || 'Rating data validation failed');
      case 429:
        throw new Error('Too many requests. Please wait a moment before trying again.');
      case 500:
        throw new Error('Server error. Please try again later.');
      case 503:
        throw new Error('Service temporarily unavailable. Please try again later.');
      default:
        throw new Error(data.message || `Request failed with status ${status}`);
    }
  } else if (error.request) {
    // Network error
    if (error.code === 'NETWORK_ERROR') {
      throw new Error('Network error. Please check your internet connection.');
    }
    if (error.code === 'TIMEOUT') {
      throw new Error('Request timed out. Please try again.');
    }
    throw new Error('Network error. Please check your connection and try again.');
  } else {
    // Request setup error
    throw new Error(error.message || 'An unexpected error occurred while processing your request');
  }
};

// Create or update a rating
export const submitRating = async (ratingData) => {
  try {
    // Transform the data to match backend expectations
    const backendData = {
      restaurantId: ratingData.restaurant || ratingData.restaurantId,
      // Extract ratings from nested structure if present, otherwise use direct properties
      overallRating: ratingData.ratings?.overall || ratingData.overall || ratingData.overallRating,
      foodQuality: ratingData.ratings?.foodQuality || ratingData.foodQuality,
      deliverySpeed: ratingData.ratings?.deliverySpeed || ratingData.deliverySpeed,
      customerService: ratingData.ratings?.customerService || ratingData.customerService,
      valueForMoney: ratingData.ratings?.valueForMoney || ratingData.valueForMoney,
      packaging: ratingData.ratings?.packaging || ratingData.packaging,
      review: ratingData.reviewText || ratingData.review,
      isAnonymous: ratingData.isAnonymous || false
    };

    // Add order if provided
    if (ratingData.order || ratingData.orderId) {
      backendData.orderId = ratingData.order || ratingData.orderId;
    }

    const response = await api.post('/ratings', backendData);
    return response.data;
  } catch (error) {
    throw handleRatingApiError(error);
  }
};



// Get restaurant ratings with pagination and filters
export const getRestaurantRatings = async (customId, options = {}) => {
  try {
    const params = {
      page: options.page || 1,
      limit: options.limit || 10,
      sortBy: options.sortBy || 'createdAt',
      sortOrder: options.sortOrder || 'desc',
      minRating: options.minRating,
      verifiedOnly: options.verifiedOnly
    };

    // Remove undefined values
    Object.keys(params).forEach(key => {
      if (params[key] === undefined) {
        delete params[key];
      }
    });

    const response = await api.get(`/ratings/restaurant/${customId}`, { params });
    return response.data;
  } catch (error) {
    throw handleRatingApiError(error);
  }
};

// Get comprehensive rating analytics and statistics
export const getRatingAnalytics = async (customId) => {
  try {
    const response = await api.get(`/ratings/restaurant/${customId}/stats`);
    return response.data;
  } catch (error) {
    throw handleRatingApiError(error);
  }
};



// Get user's rating for a specific restaurant
export const getUserRating = async (customId) => {
  try {
    const response = await api.get(`/ratings/user/restaurant/${customId}`);
    // API returns { success: true, data: { rating object } }
    return response.data.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null; // User hasn't rated this restaurant
    }
    throw handleRatingApiError(error);
  }
};



// Get top-rated restaurants
export const getTopRatedRestaurants = async (options = {}) => {
  try {
    const params = {
      limit: options.limit || 10,
      minRatings: options.minRatings || 5
    };

    const response = await api.get('/ratings/top-restaurants', { params });
    return response.data;
  } catch (error) {
    throw handleRatingApiError(error);
  }
};

// Mark a rating as helpful
export const markRatingHelpful = async (ratingId) => {
  try {
    const response = await api.post(`/ratings/${ratingId}/helpful`);
    return response.data;
  } catch (error) {
    throw handleRatingApiError(error);
  }
};

// Report a rating
export const reportRating = async (ratingId, reason) => {
  try {
    const response = await api.post(`/ratings/${ratingId}/report`, { reason });
    return response.data;
  } catch (error) {
    throw handleRatingApiError(error);
  }
};

// Delete a rating (user can delete their own rating)
export const deleteRating = async (ratingId) => {
  try {
    const response = await api.delete(`/ratings/${ratingId}`);
    return response.data;
  } catch (error) {
    throw handleRatingApiError(error);
  }
};

// Admin: Get reported ratings
export const getReportedRatings = async (options = {}) => {
  try {
    const params = {
      page: options.page || 1,
      limit: options.limit || 20
    };

    const response = await api.get('/ratings/reported', { params });
    return response.data;
  } catch (error) {
    throw handleRatingApiError(error);
  }
};

// Admin: Moderate a rating (hide, show, or dismiss report)
export const moderateRating = async (ratingId, action, adminNotes = '') => {
  try {
    const response = await api.put(`/ratings/${ratingId}/moderate`, {
      action,
      adminNotes
    });
    return response.data;
  } catch (error) {
    throw handleRatingApiError(error);
  }
};









/**
 * MENU MANAGEMENT APIs
 */

// Get restaurant menu
export const getRestaurantMenu = async (customId) => {
  try {
    const response = await api.get(`/restaurants/mymeals/${customId}`);
    return response.data.meals;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * RATING UTILITY FUNCTIONS
 */

// Validate rating data before submission
export const validateRatingData = (ratingData) => {
  const errors = [];

  // Check required fields
  if (!ratingData.restaurant && !ratingData.restaurantId) {
    errors.push('Restaurant ID is required');
  }

  // Extract ratings from either nested structure or direct properties
  const ratings = ratingData.ratings || {
    overall: ratingData.overall || ratingData.overallRating,
    foodQuality: ratingData.foodQuality,
    deliverySpeed: ratingData.deliverySpeed,
    customerService: ratingData.customerService,
    valueForMoney: ratingData.valueForMoney,
    packaging: ratingData.packaging
  };

  // Check if at least overall rating is provided
  if (!ratings.overall) {
    errors.push('Overall rating is required');
  }

  // Validate each rating score that is provided
  const ratingFields = {
    overall: ratings.overall,
    foodQuality: ratings.foodQuality,
    deliverySpeed: ratings.deliverySpeed,
    customerService: ratings.customerService,
    valueForMoney: ratings.valueForMoney,
    packaging: ratings.packaging
  };

  Object.entries(ratingFields).forEach(([field, value]) => {
    if (value !== undefined && value !== null && value !== 0) {
      if (typeof value !== 'number' || value < 1 || value > 5) {
        errors.push(`${field} rating must be between 1 and 5`);
      }
    }
  });

  // Validate review text length
  const reviewText = ratingData.reviewText || ratingData.review;
  if (reviewText && reviewText.length > 500) {
    errors.push('Review text must be 500 characters or less');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Format rating display
export const formatRating = (rating, decimals = 1) => {
  if (typeof rating !== 'number') return '0.0';
  return rating.toFixed(decimals);
};

// Get rating color class based on score
export const getRatingColorClass = (rating) => {
  if (rating >= 4.5) return 'text-green-600';
  if (rating >= 4.0) return 'text-green-500';
  if (rating >= 3.5) return 'text-yellow-500';
  if (rating >= 3.0) return 'text-orange-500';
  return 'text-red-500';
};

// Calculate average rating from ratings object
export const calculateAverageRating = (ratings) => {
  if (!ratings || typeof ratings !== 'object') return 0;

  const scores = Object.values(ratings).filter(score => typeof score === 'number');
  if (scores.length === 0) return 0;

  const sum = scores.reduce((total, score) => total + score, 0);
  return sum / scores.length;
};

// Format rating criteria names for display
export const formatCriteriaName = (criteriaKey) => {
  const criteriaNames = {
    overall: 'Overall',
    foodQuality: 'Food Quality',
    deliverySpeed: 'Delivery Speed',
    customerService: 'Customer Service',
    valueForMoney: 'Value for Money',
    packaging: 'Packaging'
  };

  return criteriaNames[criteriaKey] || criteriaKey;
};

/**
 * UTILITY FUNCTIONS
 */

// Format currency
export const formatCurrency = (amount) => {
  return `₦${amount?.toLocaleString() || '0.00'}`;
};

// Format order date
export const formatOrderDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = Math.abs(now - date) / (1000 * 60 * 60);

  if (diffInHours < 24) {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  } else if (diffInHours < 48) {
    return `Yesterday ${date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    })}`;
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
};

// Get status display properties
export const getStatusDisplay = (status) => {
  // Compare with capitalized statuses (Order enum format)
  switch (status) {
    case OrderStatus.PENDING:
      return {
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: '⏰',
        text: 'Pending'
      };
    case OrderStatus.CONFIRMED:
      return {
        color: 'bg-primary-100 text-primary-800 border-primary-200',
        icon: '✅',
        text: 'Confirmed'
      };
    case OrderStatus.DELIVERED:
      return {
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: '🚚',
        text: 'Delivered'
      };
    case OrderStatus.CANCELED:
      return {
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: '❌',
        text: 'Canceled'
      };
    case OrderStatus.FEE_REQUESTED:
      return {
        color: 'bg-orange-100 text-orange-800 border-orange-200',
        icon: '💰',
        text: 'Fee Requested'
      };
    default:
      return {
        color: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: '❓',
        text: status || 'Unknown'
      };
  }
};

// Filter orders by status
export const filterOrdersByStatus = (orders, status) => {
  if (status === 'all') return orders;
  return orders.filter(order => order.status === status);
};

// Calculate order statistics
export const calculateOrderStats = (orders) => {
  const stats = {
    total: orders.length,
    pending: 0,
    confirmed: 0,
    delivered: 0,
    canceled: 0,
    totalRevenue: 0,
    totalFoodAmount: 0,
    totalFees: 0
  };

  orders.forEach(order => {
    const status = order.status;
    stats[status] = (stats[status] || 0) + 1;

    if (status === OrderStatus.DELIVERED) {
      stats.totalRevenue += order.totalPrice || 0;
      stats.totalFoodAmount += order.foodAmount || 0;
      stats.totalFees += order.fee || 0;
    }
  });

  return stats;
};

/**
 * Cancel an order with a reason
 * @param {string} orderId - The custom ID of the order to cancel
 * @param {string} reason - The reason for cancellation
 * @returns {Promise<Object>} The cancelled order data
 */
export const cancelOrder = async (orderId, reason) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await api.post(`/orders/${orderId}/cancel`, {
      reason: reason
    }, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

/**
 * NOTIFICATION MANAGEMENT APIs
 */

// Get restaurant notifications
export const getRestaurantNotifications = async () => {
  try {
    const response = await api.get('/restaurants/notifications');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Mark notification as read
export const markNotificationAsRead = async (notificationId) => {
  try {
    const response = await api.patch(`/notifications/${notificationId}/read`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Mark all notifications as read
export const markAllNotificationsAsRead = async () => {
  try {
    const response = await api.patch('/notifications/read-all');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * AUTHENTICATION MANAGEMENT
 */

// Restaurant login
export const restaurantLogin = async (email, password) => {
  try {
    const response = await api.post('/restaurants/login', { email, password });
    const { token, restaurant } = response.data;
    if (token) {
      localStorage.setItem('token', token);
    }
    return { token, restaurant };
  } catch (error) {
    throw handleApiError(error);
  }
};

// Refresh authentication token
export const refreshToken = async () => {
  const currentToken = localStorage.getItem('token');
  if (!currentToken) {
    throw new Error('No token found');
  }

  try {
    const response = await api.post('/auth/refresh', {
      token: currentToken
    });
    const { token } = response.data;
    if (token) {
      localStorage.setItem('token', token);
    }
    return token;
  } catch (error) {
    localStorage.removeItem('token');
    throw handleApiError(error);
  }
};



export default api;
